/**
 * Command K is one shared surface with three jobs (#1466):
 *
 * Go  - pinned/recent work, governed record search and navigation.
 * Do  - acts supplied by the current page or record.
 * Ask - hand the draft and current context to the ONE Copilot surface.
 *
 * This file binds console data to @solum/ui's interaction model. It deliberately
 * owns no dialog, keyboard/listbox implementation or second Copilot renderer.
 *
 * ── IT IS THE *APPLICATION* PALETTE, AND THE WORD IS LOAD-BEARING (#2328 §7) ─
 * ROOT ruling 74071 §5 gives this file two obligations it used to hold neither
 * of, and both are discharged in `palette/providers.ts` rather than here:
 *
 *  · THE SETTINGS REGISTRY IS UNREACHABLE FROM HERE. Not filtered — unreachable.
 *    Nothing in this module's transitive imports touches `../settings/`, so
 *    there is no settings provider, no `Open Settings` result and no flag that
 *    would add one. `palette/imports.census.test.ts` asserts it in both
 *    directions. #2328 §7: *"This exclusion occurs at provider/registry
 *    composition time, not as a string filter after results are returned."*
 *
 *  · ADMINISTRATIVE DOORS ARE NOT ASKED. `applicationProviders` classifies every
 *    search tool, catalogue entity, recent and pin BEFORE anything is queried,
 *    and `searchTools` below is what remains. The previous shape queried
 *    `access.search_users` and five siblings on every keystroke and discarded
 *    the rows at render time, which ruling §5 names as the defect: *"not
 *    queried, not merely not returned."*
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useRouterState } from '@tanstack/react-router'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { buildNav } from '@solum/experience'
import type { McpToolSpec, Row } from '@solum/sdk'
import {
  Button, CommandPalette as CommandSurface, EntityIcon, commandIntent, matchesText,
  parseCommandQuery, rankCommandGroups, useDebouncedValue,
  type CommandPaletteGroup, type CommandPaletteItem,
} from '@solum/ui'
import {
  useSchema, useSemantics, useEntityRows, useEntityPage, useEntityIdentities, useRecordIdentities, useReadRoute, PAGE_SIZE,
} from '../data/queries'
import type { RecordNamingState } from '../data/recordIdentity'
import { useSolum } from '../data/client'
import { getAllRecents, recordRecent, useRecentsVersion } from './recents'
import { launchCopilot } from '../copilot/launcher'
import { usePaletteScope } from './paletteScope'
import { deriveSearchTools, runFederatedSearch } from './federatedSearch'
import { usePins } from './pins'
import { useRegistry } from '../data/registry'
import { consoleProfile, showsFleetSurfaces } from '../data/profile'
import s from './CommandPalette.module.css'
import { openActivityComposer } from './ActivityComposerHost'
import { activityContextFor, composeDoGroups } from './doGroups'
import { recordDestination } from './recordDestination'
import { elevateDestination } from './elevateDestination'
import { applicationProviders } from './palette/providers'
import { ctxFor } from '../copilot/brain'
import { usePaletteRecordActions } from '../record/EntityActions'
import { useMyAreas } from '../access/access'
import { backstageAdmitted } from './consoleLauncherActions'
import { applyView } from '../experience/governed'

/** Fallback only when a deployment has registered no search tools. */
const SEARCH_ENTITIES = ['crm_opportunity', 'core_organization', 'core_person'] as const

/**
 * The scoped list's rows a person may be SHOWN, in the order the read returned
 * them, each named by the governed title door (#2849). Only a record the door
 * answered for — named, or authorised and unnamed — is listed: an unavailable
 * one (refused, deleted, unknown) is absent, and one still resolving waits
 * rather than appearing as "Loading record…".
 */
export function scopePoolItems(
  refs: readonly { type: string; id: string }[],
  identity: (type: string, id: string) => { title: string; state: RecordNamingState },
): { id: string; label: string }[] {
  return refs.flatMap((ref) => {
    const named = identity(ref.type, ref.id)
    return named.state === 'named' || named.state === 'unnamed' ? [{ id: ref.id, label: named.title }] : []
  })
}

/** The no-search-installed GLOBAL fallback's label guess — never the scoped pool's. */
const displayOf = (row: Row): string =>
  String(row.display_name ?? row.legal_name ?? row.name
    ?? (`${row.given_name ?? ''} ${row.family_name ?? ''}`.trim() || 'Record unavailable'))

/** Collapse provider-specific opening groups into the approved two-column
 * hierarchy. The provider identities return once a query is present. */
export function commandOpeningGroups(
  goGroups: readonly CommandPaletteGroup[],
  doGroups: readonly CommandPaletteGroup[],
): CommandPaletteGroup[] {
  const unique = (items: CommandPaletteItem[]) => items
    .filter((item, index) => items.findIndex((candidate) => candidate.id === item.id) === index)
    .slice(0, 8)
  const recall = unique(goGroups
    .filter((group) => group.kind === 'recall')
    .flatMap((group) => group.items))
  const create = unique(doGroups.flatMap((group) => group.items))
  return [
    { id: 'opening-recall', label: 'Recent & pinned', items: recall, kind: 'recall', testId: 'pal-opening-recall' },
    { id: 'opening-do', label: 'Create & do', items: create, kind: 'actions', testId: 'pal-opening-do' },
  ]
}

export function CommandPalette({ open, onOpenChange }: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const navigate = useNavigate()
  const { client } = useSolum()
  const schema = useSchema()
  const semantics = useSemantics()
  const entityIdentities = useEntityIdentities()
  const scope = usePaletteScope()
  const pathname = useRouterState({ select: (state) => state.location.pathname })
  const pins = usePins()
  const registry = useRegistry()
  const fleet = showsFleetSurfaces(consoleProfile(), registry.isInstalled)
  // The Onboarding destination's gate — the same one as its nav link (#2940).
  const onboardingOffered = registry.isInstalled('portal')
  const [widened, setWidened] = useState(false)
  const [explicitScope, setExplicitScope] = useState<typeof scope>(null)
  const [candidate, setCandidate] = useState<CommandPaletteItem['record']>(undefined)
  const [rawQuery, setQuery] = useState('')
  const parsed = parseCommandQuery(rawQuery)
  const query = parsed.query

  useEffect(() => {
    if (!open) return
    setWidened(false)
    setExplicitScope(null)
    setCandidate(undefined)
    setQuery('')
  }, [open])

  const sourceScope = explicitScope ?? scope
  const scoped = sourceScope && !widened
  const selectionCount = sourceScope?.selectionCount?.() ?? 0
  /* Nothing below lists or names a record while ⌘K is CLOSED (#2849 review,
     major 2): this component is mounted on every page, so a read keyed only on
     "a scope is registered" ran on every list and record page — and on a
     subject entity every row it returned was a `record.viewed`. Federated
     search was already gated on `open`; the scope record's title, the scoped
     pool, the no-search fallback and the recents' titles are gated the same
     way. (The acts of a record highlighted while it WAS open are an actions
     question, `my_entity_actions`, not a listing or a title.) */
  const scopeRecords = useRecordIdentities(open && sourceScope?.recordId ? [{ type: sourceScope.entity, id: sourceScope.recordId }] : [])
  /* The acts the scope already offers as its own verbs: the highlighted row's
     copy of the same `none` act is not listed beside them (#2849 review). */
  const scopeTools = useMemo(
    () => scoped ? (sourceScope.verbs ?? []).flatMap((verb) => verb.tool ? [verb.tool] : []) : [],
    [scoped, sourceScope])
  const candidateActions = usePaletteRecordActions(candidate?.entity, candidate?.id, scopeTools)
  const rawNavigation = useMemo(
    () => schema.data && semantics.data
      ? buildNav(schema.data, semantics.data, entityIdentities.data) : [],
    [entityIdentities.data, schema.data, semantics.data],
  )
  /* THE GOVERNED CLASSIFICATION, WHEN THE DEPLOYMENT HAS ONE (ruling §5).
     A lookup rather than a scan, and keyed off the schema rows the palette
     already holds — `describe_schema()` sends every column of
     `core.entity_type`, so the day the column lands this map starts answering
     with no second call and no further change here. */
  const governedEntityClass = useMemo(() => {
    const byKey = new Map((schema.data?.entities ?? []).map((entity) => [entity.key, entity.palette_class]))
    return (entity: string) => byKey.get(entity)
  }, [schema.data])
  const identityByType = useMemo(
    () => new Map((entityIdentities.data ?? []).map((identity) => [identity.entity_type, identity])),
    [entityIdentities.data],
  )
  const canOpenRecord = useCallback(
    (entity: string) => identityByType.get(entity)?.navigation_eligible === true,
    [identityByType],
  )
  const canOpenPaletteRecord = useCallback(
    (entity: string) => canOpenRecord(entity)
      || (fleet && entity === 'controlplane_deployment'),
    [canOpenRecord, fleet],
  )

  /* Registered search tools are the global search universe. There is no client
     list to keep in sync with modules, and every result still came through its
     owning governed door. */
  const tools = useQuery({
    queryKey: ['mcp-tools'],
    queryFn: () => client.describeTools().catch(() => null as McpToolSpec[] | null),
    staleTime: 60_000,
  })
  const rawSearchTools = useMemo(
    () => (schema.data && Array.isArray(tools.data) ? deriveSearchTools(tools.data, schema.data.entities) : []),
    [schema.data, tools.data],
  )
  const governedToolClass = useMemo(() => {
    const byName = new Map((Array.isArray(tools.data) ? tools.data : []).map((tool) => [tool.name, tool.paletteClass]))
    return (tool: string) => byName.get(tool)
  }, [tools.data])
  /* ── THE COMPOSED APPLICATION PROVIDER LIST (ruling 74071 §5) ─────────────
   *
   * Everything the palette reaches for passes through here BEFORE it is used:
   * the fan-out list, the entity catalogue, the recents and the pins. An
   * administrative subject never becomes a query — not a `search_*` fan-out, and
   * not the `titles_of` resolution the recents do below, which is itself a read
   * of the record ruling §5 says must not be queried.
   *
   * Recents are read here rather than beside their resolution because of that
   * second half: filtering after `useRecordIdentities` would have asked the
   * question already. */
  const recentsVersion = useRecentsVersion()
  /* The launcher's backstage gate, the same predicate and the same cached
     `access.my_areas()` read the shell already makes (ROOT c.116770 G-A). */
  const areas = useMyAreas()
  const backstage = backstageAdmitted(areas.data)
  const rawRecents = useMemo(
    () => getAllRecents().map((item) => ({ entity: item.entity, id: item.id })),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- an external store, read per open and per change
    [open, recentsVersion],
  )
  const composed = useMemo(() => applicationProviders({
    tools: rawSearchTools,
    navigation: rawNavigation,
    recents: rawRecents,
    pins: pins.items,
    governedEntityClass,
    governedToolClass,
    backstage,
  }), [backstage, governedEntityClass, governedToolClass, pins.items, rawNavigation, rawRecents, rawSearchTools])
  const searchTools = composed.searchTools
  const navigation = composed.navigation

  const debounced = useDebouncedValue(query.trim(), 200)
  const federated = useQuery({
    queryKey: ['fed-search', debounced],
    enabled: open && parsed.filter !== 'actions' && parsed.filter !== 'ask' && !scoped && searchTools.length > 0 && debounced.length >= 2,
    queryFn: () => runFederatedSearch(client, searchTools, debounced),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  })
  const federatedActive = parsed.filter !== 'actions' && parsed.filter !== 'ask' && !scoped && searchTools.length > 0 && query.trim().length >= 2

  /* The legacy pools exist only for a deployment with no registered search and
     for a scoped list that did not supply its own rows. They are never the
     default global result wall. */
  const legacyActive = open && parsed.filter !== 'actions' && parsed.filter !== 'ask' && !scoped && searchTools.length === 0 && query.trim().length >= 2
  const scopeNeedsPool = Boolean(open && parsed.filter !== 'actions' && parsed.filter !== 'ask' && scoped && sourceScope && !sourceScope.items)
  const opportunities = useEntityRows('crm_opportunity', PAGE_SIZE, legacyActive)
  const organizations = useEntityRows('core_organization', PAGE_SIZE, legacyActive)
  const people = useEntityRows('core_person', PAGE_SIZE, legacyActive)
  const rowsOf = useMemo<Record<string, Row[]>>(() => ({
    crm_opportunity: (opportunities.data ?? []) as Row[],
    core_organization: (organizations.data ?? []) as Row[],
    core_person: (people.data ?? []) as Row[],
  }), [opportunities.data, organizations.data, people.data])
  /* THE SCOPED POOL is the scoped LIST's own page (#2849, ROOT c.116708 and
     c.116770, Topic 4 c.116745): read the way the list reads itself —
     `useEntityPage`, the SDK handle's page (`core.entity_page` for a module
     door) — under the list's OWN filters and order, an event filter among
     them, so the same query is usually already cached. Only a list lends a
     page: a record, workspace or draft scope lists no records. A row this
     caller could not open is not listed either — choosing it would do nothing.
     Two or more typed characters are the page's `search` only where the read
     contract declares PAGE search (`capabilities.search`; never the picker's
     lookup door), and the module answers them; elsewhere they filter the
     loaded page. Each row is named by `core.titles_of`, and one the read does
     not return or the title door will not name for this caller is ABSENT. */
  const poolScope = scopeNeedsPool && sourceScope!.page && canOpenRecord(sourceScope!.entity) ? sourceScope! : null
  const scopeEntity = poolScope?.entity ?? ''
  const scopeRoute = useReadRoute(scopeEntity)
  const scopeLimit = scopeRoute.pageLimit != null ? Math.min(PAGE_SIZE, scopeRoute.pageLimit) : PAGE_SIZE
  const poolSearches = poolScope != null && scopeRoute.capabilities?.search === true
  const scopeSearch = poolSearches && debounced.length >= 2 ? debounced : undefined
  const scopePage = useEntityPage(scopeEntity, {
    limit: PAGE_SIZE,
    filters: poolScope?.page?.filters,
    order: poolScope?.page?.order,
    ...(scopeSearch ? { search: scopeSearch } : {}),
    enabled: poolScope != null,
  })
  /* A view the list evaluates in the browser (`$me`, `empty`) lends its leaves:
     the same `applyView` the list runs, so ⌘K lists no row the list hides. */
  const clientView = poolScope?.page?.clientView
  const scopeRefs = useMemo(
    () => (clientView ? applyView(scopePage.data.rows as Row[], clientView) : scopePage.data.rows as Row[])
      .map((row) => ({ type: scopeEntity, id: String(row.id) })),
    [clientView, scopeEntity, scopePage.data.rows])
  const scopeTitles = useRecordIdentities(scopeRefs)
  const scopePool = useMemo(() => scopePoolItems(scopeRefs, scopeTitles.identity), [scopeRefs, scopeTitles.identity])

  const openRecord = useCallback((entity: string, id: string, label = '') => {
    const elevate = elevateDestination(fleet, entity, id, label)
    if (elevate) {
      recordRecent(entity, id)
      void navigate({ to: elevate.to, params: elevate.params })
      return
    }
    if (!canOpenRecord(entity)) return
    recordRecent(entity, id)
    void navigate(recordDestination(entity, id))
  }, [canOpenRecord, fleet, navigate])

  /* Recents resolve by id, so a rename or a row outside the first page remains
     truthful.
     `open` re-reads the list each invocation — and `recentsVersion` re-reads it
     when the list itself changes, which #2325 made necessary: recents are the
     caller's governed `core.user_setting` row now, so on a cold load they
     arrive AFTER the first render rather than being synchronously present in
     localStorage. Keyed on `open` alone, a palette invoked before the settings
     read landed showed no recents and never recovered. Both keys live on
     `rawRecents` above, which is where the list is now read.

     `composed.recents`, not the raw list: a recent record of an administrative
     entity is one of the RECORDS ruling §5 excludes, and it must not reach
     `useRecordIdentities` either — that call is a governed `titles_of` read of
     exactly the row in question. */
  /* …and only while ⌘K is OPEN (#2849 review): `rawRecents` is re-keyed on every
     visit, and each re-key re-asked `titles_of` about up to forty recent records
     — on a subject entity, a receipt each — with the palette closed. The cost
     is one round trip on first open. */
  const recentRefs = useMemo(
    () => open ? composed.recents.map((item) => ({ type: item.entity, id: item.id })) : [],
    [composed.recents, open],
  )
  /* Keyed on the hook's STABLE parts, never on the object it returns — that is
     new every render, and keyed on it `recents` (so every scope row's `record`)
     was rebuilt every render: with the highlight handed back below, scoped ⌘K
     over a list with rows re-rendered without end (#2849 review, major). */
  const recentRecords = useRecordIdentities(recentRefs)
  const { identity: recentIdentity, canNavigate: recentCanNavigate } = recentRecords
  const recentsPending = recentRecords.query.isPending
  const recents = useMemo(() => recentRefs
    .filter((ref) => recentCanNavigate(ref.type))
    .map((ref) => ({ ...ref, identity: recentIdentity(ref.type, ref.id) }))
    .filter((item) => recentsPending || item.identity.resolved)
    .map((item) => ({
      entity: item.type, id: item.id, label: item.identity.title,
      typeLabel: item.identity.typeLabel, iconKey: item.identity.iconKey,
    }))
    .slice(0, 8), [recentCanNavigate, recentIdentity, recentRefs, recentsPending])

  const goGroups = useMemo<CommandPaletteGroup[]>(() => {
    if (scoped && sourceScope) {
      const items = (sourceScope.items ?? scopePool)
        /* Rows the module searched with exactly the words typed keep its ranking.
           While the debounce runs the page on screen was not searched with them,
           so it is text-filtered, never offered whole. */
        .filter((item) => (scopeSearch != null && scopeSearch === query.trim()) || matchesText(query, item.label))
        .slice(0, 40)
        .map((item) => ({
          id: `scope:${sourceScope.entity}:${item.id}`,
          label: item.label,
          detail: sourceScope.label,
          kind: 'record' as const,
          record: { entity: sourceScope.entity, id: item.id, label: item.label },
          onSelect: () => sourceScope.open ? sourceScope.open(item.id) : openRecord(sourceScope.entity, item.id, item.label),
        }))
      return [{
        id: 'scope', label: sourceScope.items ? sourceScope.label
          : `${sourceScope.label} - first ${scopeLimit} ${scopeSearch ? 'matches' : 'loaded'}`,
        items, testId: 'pal-rows', kind: 'records',
      }]
    }

    const groups: CommandPaletteGroup[] = []
    const pinned = composed.pins
      .filter((pin) => pin.scope === 'navigation')
      .filter((pin) => pin.kind !== 'record' || !pin.entity || canOpenPaletteRecord(pin.entity))
      .filter((pin) => matchesText(query, pin.name, pin.type, pin.kind)).slice(0, query ? 20 : 6)
    if (pinned.length) groups.push({
      id: 'pinned', label: 'Pinned', testId: 'pal-pins', kind: 'recall',
      items: pinned.map((pin) => ({
        id: `pin:${pin.to}`, label: pin.name, detail: pin.type,
        kind: 'recall' as const,
        onSelect: () => { void navigate({ to: pin.to }) },
      })),
    })

    const recent = recents.filter((item) => matchesText(query, item.label, item.typeLabel)).slice(0, query ? 8 : 6)
    if (recent.length) groups.push({
      id: 'recent', label: 'Recent', testId: 'pal-recents', kind: 'recall',
      items: recent.map((item) => ({
        id: `recent:${item.entity}:${item.id}`, label: item.label, detail: item.typeLabel,
        icon: <EntityIcon iconKey={item.iconKey} />,
        kind: 'record' as const,
        record: { entity: item.entity, id: item.id, label: item.label },
        onSelect: () => openRecord(item.entity, item.id, item.label),
      })),
    })

    if (federatedActive) {
      for (const group of federated.data?.groups ?? []) {
        const items = group.hits.filter(() => Boolean(group.tool.entity) && canOpenPaletteRecord(group.tool.entity!)).map((hit) => ({
          id: `fed:${group.tool.name}:${hit.id}`,
          label: hit.label,
          detail: hit.detail || group.tool.label,
          icon: <EntityIcon iconKey={identityByType.get(group.tool.entity!)?.icon_key} />,
          kind: 'record' as const,
          record: { entity: group.tool.entity!, id: hit.id, label: hit.label },
          onSelect: () => openRecord(group.tool.entity!, hit.id, hit.label),
        }))
        if (items.length) groups.push({
          id: `fed:${group.tool.name}`, label: group.tool.label, items, testId: 'pal-fed-group', kind: 'records',
        })
      }
    }

    if (legacyActive) {
      const items = SEARCH_ENTITIES.filter(canOpenRecord).flatMap((entity) => (rowsOf[entity] ?? [])
        .filter((row) => matchesText(query, displayOf(row)))
        .slice(0, 40)
        .map((row) => ({
          id: `legacy:${entity}:${String(row.id)}`, label: displayOf(row),
          detail: identityByType.get(entity)?.singular_label ?? 'Record',
          icon: <EntityIcon iconKey={identityByType.get(entity)?.icon_key} />,
          kind: 'record' as const,
          record: { entity, id: String(row.id), label: displayOf(row) },
          onSelect: () => openRecord(entity, String(row.id), displayOf(row)),
        })))
      groups.push({
        id: 'legacy', label: `Records - first ${PAGE_SIZE} per type (no server search installed)`, items,
        testId: 'pal-legacy-records', kind: 'records',
      })
    }

    // The cockpit is a product destination, not a generic entity. It appears
    // only on a fleet console and only once the person searches for it; the
    // governed customer/environment hits above then route into the same stable
    // cockpit rather than the generic organization form.
    if (fleet && query.trim() && matchesText(query, 'Elevate Cockpit', 'Products', 'customers subscriptions environments')) {
      groups.unshift({
        id: 'elevate-cockpit', label: 'Products', testId: 'pal-elevate', kind: 'pages',
        items: [{
          id: 'go:elevate-cockpit', label: 'Elevate Cockpit',
          detail: 'Customers, subscriptions and environments',
          kind: 'page',
          onSelect: () => { void navigate({ to: '/ops/estate', search: {} }) },
        }],
      })
    }

    // The catalogue is a search result, never the opening wall of choices.
    if (query.trim()) {
      /* The common Onboarding destination (#2940): a place to go, found by its
         own name or what it is for. Choosing it opens the page and does nothing
         else — no invitation is sent and no access granted from here. Offered to
         a customer's own administrator too, as its nav link is; the portal
         doors decide what each caller may do there. */
      if (onboardingOffered && matchesText(query, 'Onboarding', 'invitations', 'invite people requests progress')) {
        groups.push({
          /* 'Pages', not a bar group's name: staff find it under Work and a
             customer's administrator under Your organization. */
          id: 'onboarding', label: 'Pages', testId: 'pal-onboarding', kind: 'pages',
          items: [{
            id: 'go:onboarding', label: 'Onboarding', detail: 'Invitations, requests and progress',
            kind: 'page',
            onSelect: () => { void navigate({ to: '/onboarding', search: {} }) },
          }],
        })
      }
      for (const group of navigation) {
        const items = group.entities
          .filter((entity) => matchesText(query, group.module, entity.label, entity.key))
          .map((entity) => ({
            id: `nav:${entity.key}`, label: entity.label, detail: group.module,
            icon: <EntityIcon iconKey={entity.iconKey} />,
            kind: 'page' as const,
            onSelect: () => { void navigate({ to: '/e/$entity', params: { entity: entity.key } }) },
          }))
        if (items.length) groups.push({ id: `nav:${group.module}`, label: group.module, items, kind: 'pages' })
      }
      // A gated backstage DESTINATION (People & Access): a place to go,
      // searched by its own name only — never by what it administers.
      const backstageItems = composed.destinations
        .filter((destination) => matchesText(query, destination.label))
        .map((destination) => ({
          id: `go:${destination.to}`, label: destination.label, detail: 'Administration',
          kind: 'page' as const,
          onSelect: () => { void navigate({ to: destination.to }) },
        }))
      if (backstageItems.length) groups.push({
        id: 'backstage', label: 'Administration', items: backstageItems, testId: 'pal-backstage', kind: 'pages',
      })
    }
    return groups
  }, [canOpenPaletteRecord, canOpenRecord, composed.destinations, composed.pins, federated.data?.groups, federatedActive, fleet, identityByType, legacyActive, navigation, navigate, onboardingOffered, openRecord, query, recents, rowsOf, scopeLimit, scopePool, scopeSearch, sourceScope, scoped])

  useEffect(() => {
    if (!candidate) return
    const stillVisible = goGroups.some((group) => group.items.some((item) =>
      item.record?.entity === candidate.entity && item.record.id === candidate.id))
    if (!stillVisible) setCandidate(undefined)
  }, [candidate, goGroups])

  const doGroups = useMemo<CommandPaletteGroup[]>(() => {
    const governedScope = scoped ? sourceScope : null
    const context = activityContextFor(
      governedScope,
      governedScope?.recordId
        ? scopeRecords.identity(governedScope.entity, governedScope.recordId).title
        : '',
      candidate,
    )
    const composedGroups = composeDoGroups({
      query,
      scope: scoped ? sourceScope : null,
      context,
      close: () => onOpenChange(false),
      // Keep Command Marble one of the two explicit doors into the shared
      // composer; the pure ranker receives behaviour, it does not own a door.
      openActivity: (request) => openActivityComposer(request),
    })
      .map((group) => ({
        ...group, kind: 'actions' as const,
        items: group.items.map((item) => ({ ...item, kind: 'action' as const })),
      }))
    return candidateActions.items.length
      ? [{ id: 'record-actions', label: `${candidate?.label ?? 'Record'} — actions`,
          kind: 'actions' as const, items: candidateActions.items }, ...composedGroups]
      : composedGroups
  }, [candidate, candidateActions.items, onOpenChange, query, scopeRecords, scoped, sourceScope])

  const askGroups = useMemo<CommandPaletteGroup[]>(() => {
    const clean = query.trim()
    const actionLabels = doGroups.flatMap((group) => group.items.map((item) => item.label))
    if (!clean || (parsed.filter !== 'ask' && commandIntent(clean, actionLabels) !== 'ask')) return []
    return [{ id: 'ask', label: 'Copilot', items: [{
      id: 'ask-copilot', label: `Ask: “${clean}”`,
      detail: sourceScope?.label ?? 'Current workspace', testId: 'pal-ask', kind: 'ask',
      onSelect: () => { launchCopilot({ query: clean, contextSnapshot: ctxFor(sourceScope, pathname) }) },
    }], kind: 'ask' }]
  }, [doGroups, parsed.filter, pathname, query, sourceScope])

  const allGroups = useMemo(() => [...goGroups, ...doGroups, ...askGroups], [askGroups, doGroups, goGroups])
  const activeGroups = useMemo(() => rankCommandGroups(
    query, allGroups, parsed.filter, doGroups.flatMap((group) => group.items.map((item) => item.label)),
  ), [allGroups, doGroups, parsed.filter, query])
  const opening = !rawQuery.trim() && !scoped
  const visibleGroups = useMemo(
    () => opening ? commandOpeningGroups(goGroups, doGroups) : activeGroups,
    [activeGroups, doGroups, goGroups, opening],
  )
  const placeholder = scoped ? `Find in ${sourceScope.label}, run an action, or ask…` : 'Search, run a command, or ask…'
  const empty = scoped ? 'Nothing in this context matched. Backspace widens to everywhere.' : 'Nothing reachable matched.'
  const federatedPending = federatedActive && (federated.isPending || federated.isFetching)
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent)
  const dictate = isMac ? 'fn fn to dictate' : 'Win+H to dictate'

  return open ? (
    <>
    <CommandSurface
      label="Command Marble"
      testId="command-palette"
      query={rawQuery}
      onQueryChange={setQuery}
      onClose={() => onOpenChange(false)}
      groups={visibleGroups}
      placeholder={placeholder}
      empty={empty}
      pending={federatedPending || (scopeSearch != null && scopePage.isFetching)}
      split={opening}
      scopeHints={[
        { id: 'records', label: 'Records', shortcut: '@' },
        { id: 'actions', label: 'Actions', shortcut: '>' },
        { id: 'ask', label: 'Ask', shortcut: '?' },
        { id: 'pages', label: 'Pages' },
      ]}
      onActiveItemChange={(item) => {
        if (sourceScope?.recordId) setCandidate(undefined)
        /* The kit hands the highlight back on every render (this handler is
           new each time); the same record keeps the same candidate, so a
           rebuilt row never becomes a state change (#2849 review, major). */
        else if (item?.record) {
          const next = item.record
          setCandidate((prev) => prev?.entity === next.entity && prev.id === next.id && prev.label === next.label ? prev : next)
        }
      }}
      onScopeItem={(item) => {
        if (!item.record) return
        setExplicitScope({ entity: item.record.entity, recordId: item.record.id, label: item.record.label })
        setWidened(false)
        setQuery('')
      }}
      onEscape={() => {
        if (parsed.filter) { setQuery(query); return true }
        if (explicitScope) { setExplicitScope(null); return true }
        if (scoped) { setWidened(true); return true }
        return false
      }}
      onInputKeyDown={(event) => {
        if (event.key === 'Backspace' && !query && scoped) {
          event.preventDefault()
          if (explicitScope) setExplicitScope(null)
          else setWidened(true)
        }
      }}
      context={scoped ? (
        <span className={s.scopechip} data-testid="pal-scope">
          <span>In: {sourceScope.label}</span>
          <Button size="sm" variant="ghost" aria-label="Widen scope" onClick={() => setWidened(true)}>×</Button>
        </span>
      ) : parsed.filter ? (
        <span className={s.scopechip} data-testid="pal-filter">
          <span>{parsed.filter}</span>
          <Button size="sm" variant="ghost" aria-label="Clear filter" onClick={() => setQuery(query)}>×</Button>
        </span>
      ) : undefined}
      footer={(
        <span data-testid="pal-foot">
          {scoped && `${selectionCount ? `${selectionCount} selected · ` : ''}scope: ${sourceScope.label} · backspace widens`}
          {!scoped && federatedActive && federated.data
            && `searched ${federated.data.sources - federated.data.dropped - federated.data.failed} of ${federated.data.sources} governed sources`}
          {!scoped && !federatedActive && 'records, actions, pages and Ask share this field · @ records · > actions · ? Ask'}
          <span className={s.dictate} title="Your device's built-in dictation types into this field."> · 🎤 {dictate}</span>
        </span>
      )}
    />
    {candidateActions.overlays}
    </>
  ) : null
}
